import { ChangeEvent, useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { PageTitle } from 'styles/Global/commonStyles';
import { HospitalPageLabel, DiagnosisInnerLayout, DiagnosisLayout, TwoBreadCrumbContainer, DetailPageInfoLayout, DiagnosisTapsContainer, DiagnosisItemButton } from 'styles/Global/hospitalStyles';
import { pagePaths } from 'constants/path';
import useToggle from 'hooks/CustomHooks/useToggle';
import ArrowRightSlim from 'assets/Icons/Arrow/ArrowRightSlim';

import { CLAdminPatientInfoBody } from 'pages/Admin/CL/CLPatientInfoPage/CLAdminPatientInfoBody';
import CLCaloriesInfo from './CLCaloriesInfo';
import { CLPatientInfoTabs } from '../constants';
import { CLMessageTypeEnum, CLPatientInfoTabsEnum } from '../enum';
import CLMessageDelivery from './CLMessageDelivery';
import MessageDeliverConfirmModal from 'pages/Admin/CL/modal/MessageDeliverConfirmModal';
import { ICLDailyStatistics, ICLUserInfo } from 'models/CL/CLBasicInterface';
import { useGetStatisticsByDateQueries } from 'api/Queries/CL/Statistics/useGetStatisticsByDateQueries';
import { formatExampleMessage, handleDate } from '../utils';
import moment from 'moment';
import CLNotifications from './CLNotifications';
import { useGetNotificationListQueries } from 'api/Queries/CL/Notifications/useGetNotificationListQueries';
import { NotificationItem } from 'models/CL/CLApiTypes';
import { useLoginStore } from 'store/useLoginDataStore';
import { CLNotificationsApi } from 'api/CL/CLNotificationApi';
import { SendNotificationReqData } from 'models/CL/CLApiTypes';
import { useGetUserDetailQueries } from 'api/Queries/CL/User/useGetUserDetailQueries';

const CL_USER_MANAGEMENT_LABEL = '사용자 관리';
const CL_USER_DETAIL_LABEL = '사용자 상세보기';

const CLPatientInfoPage = () => {
    const navigate = useNavigate();
    const { state } = useLocation();
    const { userId } = useParams();
    //  const queryParams = new URLSearchParams(search);

    const loginData = useLoginStore().loginData;
    const fallbackUserInfo = state as ICLUserInfo | null;
    const selectedUserId = userId || fallbackUserInfo?._id || '';
    const { data: userDetailData } = useGetUserDetailQueries(selectedUserId);
    const userInfo = userDetailData || fallbackUserInfo;

    const [tabMenu, setTabMenu] = useState(CLPatientInfoTabsEnum.CALORIES_RECORD);
    const [messageType, setMessageType] = useState<string>(CLMessageTypeEnum.MEDICAL_GUIDE);
    const [messageTitle, setMessageTitle] = useState<string>('');
    const [message, setMessage] = useState<string>('');
    const [selectFilter, setselectFilter] = useState(0);
    const [currentData, setCurrentData] = useState<ICLDailyStatistics[]>([]);
    const [messages, setMessages] = useState<NotificationItem[]>([]);
    const [offset, setOffset] = useState(0);
    const [hasMore, setHasMore] = useState(true);

    const [isMessageDeliverConfirmModal, setIsMessageDeliverConfirmModal] = useToggle();

    const { data: statisticsData } = useGetStatisticsByDateQueries(handleDate(selectFilter).startDate, handleDate(selectFilter).endDate, selectedUserId);
    
    const { data: notificationData, isLoading: notificationLoading, refetch: refetchNotificationData } = useGetNotificationListQueries(
        {
            userId: statisticsData?.userId || '',
            offset: offset,
            limit: 10
        }, 
        { enabled: tabMenu === CLPatientInfoTabsEnum.MESSAGE_DELIVERY && !!statisticsData?.userId && statisticsData.userId !== '' }
    );

    // useEffect(() => {
    //     console.log(handleDate(selectFilter));
    // }, [selectFilter]);

    useEffect(() => {
        if (statisticsData) {
            const sortedData = statisticsData.dailyStatistics.sort((a, b) => {
                return moment(b.date, 'YYYYMMDD').valueOf() - moment(a.date, 'YYYYMMDD').valueOf();
            });
            setCurrentData(sortedData);
        }
    }, [statisticsData]);

    useEffect(() => {
        // MESSAGE_DELIVERY 탭이 아닌 경우 메시지 초기화
        if (tabMenu !== CLPatientInfoTabsEnum.MESSAGE_DELIVERY) {
            setMessages([]);
            setOffset(0);
            setHasMore(true);
            return;
        }
        
        // MESSAGE_DELIVERY 탭이고 notificationData가 있는 경우
        if (notificationData && notificationData.items) {
            if (offset === 0) {
                // 첫 번째 로드
                setMessages(notificationData.items);
            } else {
                // 추가 로드
                setMessages(prev => [...prev, ...notificationData.items]);
            }
            
            // 더 로드할 데이터가 있는지 확인
            setHasMore(notificationData.nextOffset !== null);
        } else {
            if (offset === 0) {
                setMessages([]);
            }
        }
    }, [notificationData, tabMenu, offset]);

    const handleMessageType = (type: string) => {
        setMessageType(type);
    };

    const handleMessage = (e: ChangeEvent<HTMLTextAreaElement>) => {
        const maxLength = 100;
        const value = e.target.value;

        if (value.length > maxLength) {
            return;
        }

        setMessage(value);
        const title = formatExampleMessage(messageType, loginData.hospitalName || '');
        setMessageTitle(title);
    };

    const handleLoadMore = () => {
        if (notificationData && notificationData.nextOffset !== null) {
            setOffset(notificationData.nextOffset);
        }
    };

    const handleSendMessage = async () => {
        if (!selectedUserId) return;

        const rqData: SendNotificationReqData = {
            userId: selectedUserId,
            title: messageTitle,
            message: message,
            type: messageType,
        };
        try {
            await CLNotificationsApi.sendNotification(rqData, process.env.REACT_APP_CL_ADMIN_KEY!);
            setMessageTitle('');
            setMessage('');
            setIsMessageDeliverConfirmModal();
            setOffset(0);
            refetchNotificationData(); // 메시지 리스트 강제 갱신
        } catch (error) {
            // TODO: 에러 처리 필요시 추가
            console.error(error);
        }
    };

    return (
        <DiagnosisLayout>
            {isMessageDeliverConfirmModal && userInfo && (
                <MessageDeliverConfirmModal
                    toggle={setIsMessageDeliverConfirmModal}
                    userInfo={userInfo}
                    message={message}
                    handleSendMessage={handleSendMessage}
                />
            )}
            {/* {!loading && currentData && ( */}
            {userInfo && (
                <DiagnosisInnerLayout>
                    <TwoBreadCrumbContainer>
                        <HospitalPageLabel onClick={() => navigate(pagePaths.CLAdminManagePatient)}>{CL_USER_MANAGEMENT_LABEL}</HospitalPageLabel>
                        <IconWrapper>
                            <ArrowRightSlim />
                        </IconWrapper>
                        <HospitalPageLabel onClick={() => window.location.reload()}>{CL_USER_DETAIL_LABEL}</HospitalPageLabel>
                    </TwoBreadCrumbContainer>
                    <PageTitle $marginbottom='20px'>{CL_USER_DETAIL_LABEL}</PageTitle>
                    <DetailPageInfoLayout>
                        <CLAdminPatientInfoBody userInfo={userInfo} statisticsData={statisticsData} />
                    </DetailPageInfoLayout>
                    <DiagnosisTapsContainer $topMargin={'40px'} $bottomMargin={'32px'}>
                        {CLPatientInfoTabs.map((item, idx) => {
                            return (
                                <DiagnosisItemButton key={idx} onClick={() => setTabMenu(idx)} $idx={idx} $selected={tabMenu === idx ? tabMenu : undefined}>
                                    {item}
                                </DiagnosisItemButton>
                            );
                        })}
                    </DiagnosisTapsContainer>

                    {tabMenu === CLPatientInfoTabsEnum.CALORIES_RECORD && (
                        <CLCaloriesInfo
                            selectFilter={selectFilter}
                            setselectFilter={setselectFilter}
                            statisticsData={currentData}
                            startDate={handleDate(selectFilter).startDate}
                            endDate={handleDate(selectFilter).endDate}
                        />
                    )}
                    {tabMenu === CLPatientInfoTabsEnum.MESSAGE_DELIVERY && (
                        <Wrap>
                            <CLMessageDelivery
                                messageType={messageType}
                                handleMessageType={handleMessageType}
                                message={message}
                                setMessage={handleMessage}
                                onSendMessageModal={setIsMessageDeliverConfirmModal}
                                hospitalName={loginData.hospitalName || ''}
                            />
                            <CLNotifications messages={messages} onLoadMore={handleLoadMore} hasMore={hasMore} isLoading={notificationLoading} />
                        </Wrap>
                    )}
                {/* {tabMenu === CLPatientInfoTabs.MESSAGE_DELIVERY && <CLMessageDelivery />} */}
                {/* 
                  
                   
                    <Divider>
                        <PaginationHeader>진료 내역</PaginationHeader>
                    </Divider>
                    <DiagnosisCountText>총 {patientInfoInitData.diagnosisIdList.length}건의 진료</DiagnosisCountText>
                    <ViewDetailPatientPagination
                        handleSorting={handleSorting}
                        sortDirections={sortDirections}
                        currentPage={currentPage}
                        totalPages={totalPages}
                        pageGroup={pageGroup}
                        handlePageGroup={handlePageGroup}
                        handlePageChange={setPage}
                        currentData={currentData}
                    /> */}
                </DiagnosisInnerLayout>
            )}
            {/* )} */}
        </DiagnosisLayout>
    );
};

export default CLPatientInfoPage;

const Wrap = styled.div`
    display: flex;
    flex-direction: column;
    gap: 20px;
`;

const IconWrapper = styled.div`
    display: flex;
    align-items: center;
    margin-bottom: 8px;
`;
